import { useContext } from 'react';
import PropTypes from 'prop-types';
import { CURRENCY_SYMBOLS } from 'components/common/constants';
import { CurrencyIcon } from 'components/common/CurrencyIcon/CurrencyIcon';
import i18nContext from 'components/i18n-context';
import { getAccountTitle } from 'components/utils';
import { CRYPTO_CURRENCY_PRECISION, CURRENCIES, isStableCoin } from 'services/currenciesConstant';
import { amountFormattedValue, formatAccountNumber } from 'services/utils';
import { ReactComponent as ArrowRightIcon } from 'assets/new-arrow-right.svg';
import './AccountCardSelector.scss';

export const AccountCardSelector = ({
  type,
  accounts = [],
  selectedAccount = null,
  onAccountSelect,
  placeholder,
  isSelectingAccount = false,
  onToggleSelection
}) => {
  const i18n = useContext(i18nContext);

  const handleAccountClick = (accountData) => {
    if (onAccountSelect) {
      onAccountSelect(accountData);
    }
    if (onToggleSelection) {
      onToggleSelection(false);
    }
  };

  const handleKeyDown = (event, callback) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  const renderAccountCard = (accountData, isClickable = false, showArrow = false) => {
    const isCrypto = accountData?.isCryptoAccount;
    const accountCurrency = accountData?.currency;
    const accountBalance = accountData?.balance;
    const accountReserved = accountData?.reserved;
    const accountNetwork = accountData?.networkCode;
    const account = accountData?.account;
    const isCard = accountData?.isCardAccount;
    const isManual = accountData?.isManualAccount;

    return (
      <div
        className={`account-card-selector accounts-page ${isClickable ? 'clickable' : ''}`}
        onClick={isClickable ? () => handleAccountClick(accountData) : undefined}
        onKeyDown={isClickable ? (e) => handleKeyDown(e, () => handleAccountClick(accountData)) : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
      >
        <div className={'left-side'}>
          <div className={'currency-wrapper'}>
            <CurrencyIcon
              isCardAccount={isCard}
              currencyIcon={
                isCrypto && isStableCoin(accountCurrency)
                  ? CURRENCIES[accountCurrency]?.networks?.[accountNetwork]?.icon || null
                  : CURRENCIES[accountCurrency]?.icon || CURRENCIES.OTHERS?.icon || null
              }
            />
            <p className={`${isCrypto ? 'crypto-currency-code' : 'currency-code'}`}>{accountCurrency}</p>
            {isCrypto && isStableCoin(accountCurrency) && accountNetwork && (
              <p className={'currency-code'}>{accountNetwork}</p>
            )}
          </div>
          <div className={'balance-info available'}>
            <p className={'balance-label'}>{i18n.getMessage('accounts.card.available')}:</p>
            <p className={'balance-value'}>
              {isCrypto
                ? `${amountFormattedValue(accountBalance, CRYPTO_CURRENCY_PRECISION)} ${accountCurrency}`
                : `${CURRENCY_SYMBOLS[accountCurrency] || '$'}${amountFormattedValue(accountBalance, 2)}`}
            </p>
          </div>
          <div className={'balance-info pending'}>
            <p className={'balance-label'}>{i18n.getMessage('accounts.card.pending')}:</p>
            <p className={'balance-label'}>
              {isCrypto
                ? `${amountFormattedValue(accountReserved, CRYPTO_CURRENCY_PRECISION)} ${accountCurrency}`
                : `${CURRENCY_SYMBOLS[accountCurrency] || '$'}${amountFormattedValue(accountReserved, 2)}`}
            </p>
          </div>
          <div className={'account-wrapper'}>
            <p className={'account-number'}>
              {isCard || isManual ? getAccountTitle(account) : formatAccountNumber(getAccountTitle(account))}
            </p>
          </div>
        </div>
        {showArrow && (
          <div className={'arrow-icon-wrapper'}>
            <ArrowRightIcon className={'arrow-icon'} />
          </div>
        )}
      </div>
    );
  };

  if (type === 'selector') {
    if (isSelectingAccount) {
      return (
        <div className={'account-card-selector-container selecting'}>
          <div className={'account-cards-grid'}>
            {accounts.map((acc, index) => (
              <div key={index} className={'account-card-item'}>
                {renderAccountCard(acc, true)}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className={'account-card-selector-container'}>
        {!selectedAccount && placeholder ? (
          <div
            className={'account-card-selector placeholder'}
            onClick={() => onToggleSelection && onToggleSelection(true)}
            onKeyDown={(e) => handleKeyDown(e, () => onToggleSelection && onToggleSelection(true))}
            role={'button'}
            tabIndex={0}
          >
            <p className={'placeholder-text'}>{placeholder}</p>
          </div>
        ) : (
          <div
            className={'selected-account-wrapper'}
            onClick={() => onToggleSelection && onToggleSelection(true)}
            onKeyDown={(e) => handleKeyDown(e, () => onToggleSelection && onToggleSelection(true))}
            role={'button'}
            tabIndex={0}
          >
            {renderAccountCard(selectedAccount || {}, false, true)}
          </div>
        )}
      </div>
    );
  }

  return renderAccountCard({}, false);
};

AccountCardSelector.propTypes = {
  type: PropTypes.string,
  accounts: PropTypes.array,
  selectedAccount: PropTypes.object,
  onAccountSelect: PropTypes.func,
  placeholder: PropTypes.string,
  isSelectingAccount: PropTypes.bool,
  onToggleSelection: PropTypes.func
};
